import { useCategoryItemContext } from "../../../../../services/context/CategoryItemContext";
import { useLoyaltyContext } from "../../../../../services/hooks/loyalty/useLoyaltyContext";
import { useOpenGuestBuiltOffer } from "../../../../../services/hooks/loyalty/useOpenGuestBuiltOffer";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../services/hooks/useReduxHooks";
import { useFixedBundleSum } from "../../../../../services/hooks/loyalty/useFixedBundleSum";
import { useOfferDescriptionHtml } from "../../../../../services/hooks/loyalty/useOfferDescriptionHtml";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import { selectIsOfferApplied } from "../../../../../services/redux/selectors/cartSelectors";
import { selectAppliedOfferGraphicEntityIds } from "../../../../../services/redux/selectors/loyaltyCartSelectors";
import { selectRedeemedLoyaltyOffer } from "../../../../../services/redux/selectors/loyaltyOfferSelectors";
import { removeLoyaltyOffer } from "../../../../../services/redux/slices/cartLoyaltyV2Slice";
import { numberWithCurrency } from "../../../../../utils/help/pricing/pricing";
import { LoyaltyItemOffer } from "../../../../../utils/LoyaltyOfferClass/LoyaltyItemOffer";
import { fixedBundleTotal } from "../../../../../utils/loyalty/offerItemPricing";
import { LoyaltyValueType } from "../../../../../utils/loyaltyTypes";
import type LoyaltyOffer from "../../../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import type { LoyaltyOfferGraphic } from "../../../../../utils/loyaltyTypes";

type CategoryOfferCardView = {
  /** The card falls back to this offer's badge when the collage draws nothing. */
  offer: LoyaltyOffer;
  title: string;
  description?: string;
  /** "Price on selection", or the set price when the offer already has one. */
  priceLabel: string;
  /** The items' own total, struck through beside the set price. */
  priceBeforeLabel?: string;
  graphics: LoyaltyOfferGraphic[];
  /** Narrows the collage to the chosen items once this offer is applied. */
  includeEntityIds?: string[];
  isApplied: boolean;
  /** Opens the offer page, where the guest assembles the offer. */
  openOffer: () => void;
  removeOffer: () => void;
};

/**
 * Display data for a guest-built offer rendered as a menu card.
 *
 * Returns undefined for an ordinary menu item, so the card components can fall
 * back to their item rendering. Hooks run either way to keep their order fixed.
 */
export const useCategoryOfferCard = (): CategoryOfferCardView | undefined => {
  const { offer } = useCategoryItemContext();
  const dispatch = useAppDispatch();
  const { t } = useTranslationI18();
  const { lang } = useLoyaltyContext();
  const openGuestBuiltOffer = useOpenGuestBuiltOffer();
  const currency = useAppSelector((state) => state.config.currency);
  const redeemedOffer = useAppSelector(selectRedeemedLoyaltyOffer);
  const isOfferApplied = useAppSelector(selectIsOfferApplied);
  const appliedGraphicEntityIds = useAppSelector(
    selectAppliedOfferGraphicEntityIds
  );
  const resolvedDescription = useOfferDescriptionHtml(
    offer,
    offer?.getDescription(lang)
  );
  /*
   * What the items cost outside the bundle. Only knowable when the bundle is
   * fixed — a bundle the guest chooses from has no total until they choose —
   * and only worth showing when the bundle actually saves them something.
   */
  const bundleSum = useFixedBundleSum(offer);

  if (!offer) {
    return undefined;
  }

  /*
   * The total only exists once the guest has picked the items, so the card
   * promises a price rather than quoting one. A set-price offer with no buy
   * requirement is the exception: its price is the whole offer.
   */
  const hasSetPrice =
    offer.valueType === LoyaltyValueType.NEW_VALUE &&
    !offer.hasBuyRequirement();

  /*
   * A fixed bundle can be quoted in full — both what it costs and what its
   * items cost apart — whatever the offer's value type, because the item set
   * is known before the guest touches anything.
   */
  const bundleTotal =
    bundleSum !== undefined && offer instanceof LoyaltyItemOffer
      ? fixedBundleTotal(offer, bundleSum)
      : undefined;

  const isApplied = isOfferApplied && redeemedOffer?.id === offer.id;

  return {
    offer,
    title: offer.getTitle(lang),
    description: resolvedDescription,
    priceLabel:
      bundleTotal !== undefined
        ? numberWithCurrency(bundleTotal, currency ?? "")
        : hasSetPrice
        ? numberWithCurrency(Number(offer.value), currency ?? "")
        : t("common:price-on-selection"),
    priceBeforeLabel:
      bundleTotal !== undefined &&
      bundleSum !== undefined &&
      bundleSum > bundleTotal
        ? numberWithCurrency(bundleSum, currency ?? "")
        : undefined,
    graphics: offer.uiConfig?.graphics ?? [],
    includeEntityIds: isApplied ? appliedGraphicEntityIds : undefined,
    isApplied,
    openOffer: () => openGuestBuiltOffer(offer),
    removeOffer: () => {
      dispatch(removeLoyaltyOffer());
    },
  };
};
